import { availableQuantity, isRunningLow } from "@dentacore/shared-types";
import type { InventoryItem, InventoryKind, MaterialScope } from "@dentacore/shared-types";

import type { NumberFieldRules } from "../ui/number-field";

/**
 * The rules the Inventory screen draws by, in one place for its four parts —
 * the list, the item's panel, the tray panel and the Add dialog.
 *
 * They were constants inside the Settings panel this screen grew out of
 * (STORY-109b). That panel is gone since the calm pass (IV1–IV11), and the
 * rules outlived it unchanged.
 */

/** A pack holds at least one use. Bounds, not a clinic's data. */
export const USES: NumberFieldRules = { min: 1, integer: true, unit: "uses" };

/**
 * A count in uses.
 *
 * **Floored at zero here and only here.** A *stored* count may go negative —
 * TP14 is explicit that stock refuses nothing and a negative figure means "this
 * is stale, correct it". But nobody *types* a negative opening count or a
 * negative full shelf, and letting them would turn a typo into the very state
 * the screen draws as a warning.
 */
export const COUNT: NumberFieldRules = { min: 0, integer: true, unit: "uses" };

/**
 * How many uses to add or take off (IC5). **Always a positive number** — the
 * button says which way it goes, so nobody has to type a minus sign, and zero
 * moves nothing (the server refuses it too).
 *
 * The count itself may still go negative (TP14): taking four off a shelf the
 * system thinks holds two is a legitimate thing to record.
 */
export const AMOUNT: NumberFieldRules = { min: 1, integer: true, unit: "uses" };

export const KINDS: readonly { value: InventoryKind; label: string }[] = [
  { value: "COUNTABLE", label: "Counted" },
  { value: "BULK", label: "Bulk" },
  { value: "REUSABLE", label: "Reusable" },
];

export const kindLabel = (kind: InventoryKind): string =>
  KINDS.find((k) => k.value === kind)?.label ?? kind;

/**
 * Per visit or per tooth (TP15), in words rather than the enum's. **"Per visit"
 * means the whole appointment** (TP16) — knowingly slightly wrong for a visit
 * spanning two quadrants, and affordable only because stock refuses nothing.
 */
export const SCOPES: readonly { value: MaterialScope; label: string }[] = [
  { value: "PER_VISIT", label: "Once per visit" },
  { value: "PER_TOOTH", label: "For each tooth" },
];

/**
 * Is this item worth somebody's attention right now?
 *
 * One function for the row's warm dot, the tab's "N low" and the card's line,
 * so the three cannot disagree about what "low" means — and **the same rule
 * the server's notice uses**, from `@dentacore/shared-types`: the item's own
 * threshold when it has one (IC2), otherwise a quarter of a full shelf. It was
 * a second copy here until STORY-174, which is how two copies drift.
 */
export const isLow = (item: InventoryItem): boolean => isRunningLow(item);

/**
 * The two figures a row shows (IC1): **whole packs still sealed, and what is
 * left in the one that is open** — 450 uses in boxes of 200 is two boxes and
 * 50 in the third. Both come from the one stored count, in uses.
 *
 * - A pack of one use (a tube) has no "open pack": every one is whole.
 * - A count that divides exactly has no open pack either.
 * - **A negative count is shown as it is** (TP14), in the open-pack column,
 *   with no packs — "-4 uses" says the figure is stale; "-1 packs" says nothing.
 */
export function packSplit(item: InventoryItem): { sealed: number | null; open: string | null } {
  const per = item.usesPerStockUnit;
  const count = item.quantityOnHand;
  if (count < 0) return { sealed: null, open: usesText(count) };
  if (per <= 1) return { sealed: count, open: null };
  const left = count % per;
  return {
    sealed: Math.floor(count / per),
    open: left === 0 ? null : `${String(left)} of ${String(per)}`,
  };
}

/** "12 uses", "1 use", "-4 uses" — a negative is written as it is (TP14). */
export const usesText = (count: number): string =>
  `${String(count)} use${Math.abs(count) === 1 ? "" : "s"}`;

/**
 * What this item is counted in (FX22): **a bulk item in whole packs** — adding
 * one adds a box, and its alert is in boxes — everything else in uses.
 */
export const unitOf = (item: Pick<InventoryItem, "kind">): "pack" | "use" =>
  item.kind === "BULK" ? "pack" : "use";

/**
 * How many uses one pack is (QA4-D2): a counted item's own pack, or a bulk
 * item's estimate (MC-D4), since a bulk item stores packs. One for anything
 * else, which is what "no pack to speak of" means.
 */
export const usesPerPack = (
  item: Pick<InventoryItem, "kind" | "usesPerStockUnit" | "estimatedUses">,
): number =>
  item.kind === "BULK"
    ? Math.max(1, item.estimatedUses ?? 1)
    : item.kind === "COUNTABLE"
      ? Math.max(1, item.usesPerStockUnit)
      : 1;

/**
 * A number typed in packs or uses, as the item stores it (QA4-D2) — or `null`
 * when it cannot be stored: a bulk item counts whole packs, so 30 gloves of a
 * box of 100 is not a count it can hold.
 */
export function toStored(
  item: Pick<InventoryItem, "kind" | "usesPerStockUnit" | "estimatedUses">,
  amount: number,
  typedIn: "pack" | "use",
): number | null {
  const per = usesPerPack(item);
  if (typedIn === unitOf(item)) return amount;
  if (typedIn === "pack") return amount * per; // packs into a count in uses
  return amount % per === 0 ? amount / per : null; // uses into whole packs
}

/**
 * The other unit beside a count, so a shelf reads both ways (QA4-D2):
 * "3 packs" beside 300 uses, "about 300 uses" beside 3 packs of gloves.
 * Null when there is no pack to speak of.
 */
export function otherUnitText(
  item: Pick<InventoryItem, "kind" | "usesPerStockUnit" | "estimatedUses">,
  count: number,
): string | null {
  const per = usesPerPack(item);
  if (per <= 1) return null;
  if (unitOf(item) === "pack") return `about ${usesText(count * per)}`;
  const packs = Math.floor(Math.abs(count) / per);
  const left = Math.abs(count) % per;
  if (count < 0) return null;
  return left === 0
    ? `${String(packs)} pack${packs === 1 ? "" : "s"}`
    : `${String(packs)} pack${packs === 1 ? "" : "s"} and ${usesText(left)}`;
}

/** "3 packs", "1 use" — a count in the item's own unit. */
export const countText = (item: Pick<InventoryItem, "kind">, count: number): string =>
  `${String(count)} ${unitOf(item)}${Math.abs(count) === 1 ? "" : "s"}`;

/** On the shelf and not promised to a booking (FX26). Negative is how many are missing. */
export const available = (item: InventoryItem): number => availableQuantity(item);

/**
 * What a pack is: "box of 20, 200 per pack", or just "tube".
 *
 * **Counts are in uses and the pack is separate**, because the two answer
 * different questions — how much is left, and what you order. Collapsing them
 * is how the twentieth-of-a-tube arithmetic TP7 rejected gets back in.
 */
export const packText = (item: InventoryItem): string =>
  item.kind === "BULK"
    ? "counted in packs"
    : item.usesPerStockUnit > 1
      ? `${String(item.usesPerStockUnit)} uses per pack`
      : "one use per pack";

export const plural = (count: number, noun: string): string =>
  `${String(count)} ${noun}${count === 1 ? "" : "s"}`;
